import { useState, useEffect, useCallback } from 'react'
import { api } from '../lib/api'
import toast from 'react-hot-toast'

import { Badge, MarketBadge, Modal, Input, Select, Textarea, Empty, LinkCell } from '../components/UI'
import { ViewWinning } from '../components/ViewModal'
import { useColumns, ColumnPicker } from '../components/ColumnPicker'
import { useAuth } from '../hooks/useAuth'
import { can } from '../lib/permissions'
import { useResizableColumns } from '../hooks/useResizableColumns'
import { MARKETS, ROLES } from '../lib/constants'

const ACTION_LABELS = {
  view:'View', edit:'Edit', create:'Create', delete:'Delete',
  generate:'Generate', audit:'Audit', wetracked:'Wetracked', checklist:'Checklist', adapt:'Adapt', resources:'Resources',
}

const ALL_SECTIONS = [
  { id:'products',   icon:'◈', label:'Product Research', actions:['view','edit','create','delete','generate','resources'] },
  { id:'funnel',     icon:'⬡', label:'Funnel Building',  actions:['view','edit','create','delete','audit','wetracked','checklist','resources'] },
  { id:'creatives',  icon:'◐', label:'Creatives',        actions:['view','edit','create','delete','resources'] },
  { id:'assetprep',  icon:'🎨', label:'Asset Prep',       actions:['view','edit','create','delete'] },
  { id:'adcopy',     icon:'◻', label:'Ad Copy',          actions:['view','edit','create','delete','adapt','resources'] },
  { id:'scheduling', icon:'◷', label:'Ad Scheduling',    actions:['view','edit','create','delete','resources'] },
  { id:'winning',    icon:'◆', label:'Winning Products', actions:['view','edit','create','delete','resources'] },
  { id:'creativerequests', icon:'🎬', label:'Creative Requests', actions:['view','edit','create','delete'] },
  { id:'fbinfra', icon:'🌐', label:'FB Infrastructure', actions:['view','edit','create','delete'] },
  { id:'pricecalc',  icon:'🧮', label:'Price Calculator', actions:['view'] },
  { id:'pricingagent', icon:'🤖', label:'Pricing Agent',  actions:['view'] },
  { id:'reports',    icon:'📊', label:'Reports',          actions:['view'] },
  { id:'payroll',    icon:'💵', label:'Payroll',          actions:['view','edit','create','delete'] },
  { id:'general',    icon:'📦', label:'General Resources', actions:['view'] },
]

// ── WINNING PRODUCTS ─────────────────────────────────────────
export function WinningProducts({ market }) {
  const { user } = useAuth()
  const isOwner = user?.role === 'owner'
  const perms = (typeof user?.permissions === 'object' && user?.permissions) ? user.permissions : {}
  const canRequest = isOwner || can(perms, 'creativerequests', 'create')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [products, setProducts] = useState([])
  const [brandMap, setBrandMap] = useState({})
  const { cols: colVis, isVisible, toggle: colToggle, reset: colReset } = useColumns('winning')
  const { getThProps, getTdProps, Resizer } = useResizableColumns('winning')

  const load = useCallback(async () => {
    setLoading(true)
    try { setRows(await api.getSection('winning', market)) }
    catch (err) { toast.error(err.message) }
    setLoading(false)
  }, [market])

  useEffect(() => { load() }, [load])
  useEffect(() => { api.getProducts().then(setProducts).catch(()=>{}) }, [])
  useEffect(() => {
    // Build product_id -> brand_name from funnel rows so the request list is recognisable
    api.getSection('funnel').then(rows => {
      const m = {}
      for (const r of rows) { if (r.brand_name && !m[r.product_id]) m[r.product_id] = r.brand_name }
      setBrandMap(m)
    }).catch(()=>{})
  }, [])

  function openAdd() { setForm({ market: market||'SE' }); setModal('add') }
  function openEdit(r) { setForm(r); setModal(r) }
  const [reqModal, setReqModal] = useState(null)
  const [reqForm, setReqForm] = useState({})
  const [reqSaving, setReqSaving] = useState(false)
  function openReq(r) {
    setReqForm({ product_id: r.product_id, market: r.market, reference_ad_link: r.creative_file_link||'', creative_type:'Video', quantity:3, priority:'Normal', status:'Requested', brief:'' })
    setReqModal(r)
  }
  const setReq = k => e => setReqForm(f => ({ ...f, [k]: e.target.value }))
  async function saveReq() {
    setReqSaving(true)
    try {
      await api.createSection('creative-requests', reqForm)
      toast.success('Creative request created!'); setReqModal(null)
    } catch (err) { toast.error(err.message) }
    setReqSaving(false)
  }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') await api.createSection('winning', form)
      else await api.updateSection('winning', modal.id, form)
      toast.success('Saved!'); setModal(null); load()
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function del(id) {
    if (!window.confirm('Remove from winning products?')) return
    try { await api.deleteSection('winning', id); toast.success('Removed'); load() }
    catch (err) { toast.error(err.message) }
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))
  const campaignName = r => [r.market, r.brand_name, r.ad_account, r.be_roas?`${r.be_roas}x`:null, r.profit?`€${r.profit}`:null].filter(Boolean).join(' | ')
  const WINNING_COLS = [
    { key:'market', label:'Market', render: r => <MarketBadge market={r.market} /> },
    { key:'product_id', label:'Product ID', render: r => <strong>{r.product_id}</strong> },
    { key:'brand_name', label:'Brand', render: r => r.brand_name||'—' },
    { key:'be_roas', label:'BE ROAS', render: r => <span className="td-roas">{r.be_roas?`${r.be_roas}x`:'—'}</span> },
    { key:'profit', label:'Profit', render: r => <span className={`td-num ${r.profit>=0?'td-profit-pos':'td-profit-neg'}`}>{r.profit?`€${r.profit}`:'—'}</span> },
    { key:'creative_file_link', label:'Creative', render: r => <LinkCell url={r.creative_file_link} /> },
    { key:'funnel_link', label:'Funnel Link', render: r => <LinkCell url={r.funnel_link} /> },
    { key:'ad_account', label:'Ad Account', render: r => r.ad_account||'—' },
    { key:'campaign_name', label:'Campaign Name', render: r => <span style={{fontWeight:600,fontSize:12}}>{campaignName(r)}</span> },
    { key:'date_added_as_winner', label:'Date Added', render: r => r.date_added_as_winner||'—' },
    { key:'last_creative_added_date', label:'Last Creative', render: r => r.last_creative_added_date||'—' },
    { key:'comments', label:'Comments', render: r => <span className="td-wrap">{r.comments||'—'}</span> },
  ]
  const winVisible = WINNING_COLS.filter(c => isVisible(c.key))

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className="toolbar">
        <div className="toolbar-left">
          <span className="toolbar-title">🏆 Winning Products</span>
          {market && <MarketBadge market={market} />}
          <span className="toolbar-sub">{rows.length} winners</span>
        </div>
        <div style={{display:'flex',gap:8}}>
          <ColumnPicker section="winning" cols={colVis} toggle={colToggle} reset={colReset} />
          <button className="btn btn-primary" onClick={openAdd}>+ Add Winner</button>
        </div>
      </div>
      <div className="table-wrap" style={{flex:1,overflow:'auto'}}>
        {loading ? <Empty icon="⏳" title="Loading..." /> : rows.length === 0
          ? <Empty icon="🏆" title="No winning products yet" sub="Add your best performers here" />
          : <table className="data-table"><thead><tr>
              {winVisible.map((c, ci) => <th key={c.key} {...getThProps(c.key, ci, winVisible)}>{c.label}<Resizer colKey={c.key} index={ci}/></th>)}
              <th style={{width:160,textAlign:'center',position:'sticky',right:0,zIndex:6,background:'var(--bg3)',borderLeft:'2px solid var(--border2)'}}>Actions</th>
            </tr></thead>
            <tbody>{rows.map((r, ri) => (
              <tr key={r.id} className="winning-row">
                {winVisible.map((c, ci) => <td key={c.key} {...getTdProps(c.key, ci, winVisible, ri)}>{c.render(r)}</td>)}
                <td style={{position:'sticky',right:0,zIndex:2,background:'var(--bg2)',borderLeft:'2px solid var(--border2)',padding:'6px 8px'}}>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:4,width:148}}>
                    <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>setViewRow(r)}>👁 View</button>
                    <button className="btn btn-secondary" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>openEdit(r)}>✏️ Edit</button>
                    <button className="btn btn-danger" style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',justifyContent:'center'}} onClick={()=>del(r.id)}>🗑 Remove</button>
                    {canRequest && <button style={{padding:'4px 6px',fontSize:11,whiteSpace:'nowrap',display:'flex',alignItems:'center',justifyContent:'center',background:'#7C3AED',color:'#fff',border:'none',borderRadius:6,fontWeight:600,cursor:'pointer'}} onClick={()=>openReq(r)}>🎬 Request</button>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>}
      </div>
      <ViewWinning row={viewRow} onClose={() => setViewRow(null)} />
      {reqModal && <Modal title="Request More Creatives" onClose={() => setReqModal(null)}
        footer={<><button className="btn btn-ghost" onClick={() => setReqModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveReq} disabled={reqSaving}>{reqSaving?'Creating...':'Create Request'}</button></>}>
        <div style={{fontSize:12,color:'var(--text3)',marginBottom:12}}>Winner: <strong style={{color:'var(--text)'}}>{reqModal.product_name} — {reqModal.market}</strong></div>
        <Input label="Winning Ad Reference Link" type="url" value={reqForm.reference_ad_link||''} onChange={setReq('reference_ad_link')} />
        <div className="form-row">
          <Select label="Type" options={['Video','Image','Both']} value={reqForm.creative_type||'Video'} onChange={setReq('creative_type')} />
          <Input label="Quantity" type="number" min="1" value={reqForm.quantity||3} onChange={setReq('quantity')} />
        </div>
        <Select label="Priority" options={['Low','Normal','High']} value={reqForm.priority||'Normal'} onChange={setReq('priority')} />
        <Textarea label="Brief / Angle Notes" value={reqForm.brief||''} onChange={setReq('brief')} style={{minHeight:80}} />
      </Modal>}
      {modal && <Modal title={modal==='add'?'Add Winner':'Edit Winner'} onClose={()=>setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        <div className="form-row">
          <Select label="Brand" options={Object.keys(brandMap).map(pid=>({value:pid,label:brandMap[pid]}))} value={form.product_id||''} onChange={e=>setForm(f=>({...f, product_id: e.target.value}))} />
          <Input label="Product ID" value={form.product_id||''} readOnly disabled />
          <Select label="Market" options={MARKETS} value={form.market||''} onChange={set('market')} />
        </div>
        <div className="form-row">
          <Input label="Date Added as Winner" type="date" value={form.date_added_as_winner||''} onChange={set('date_added_as_winner')} />
          <Input label="Last Creative Added Date" type="date" value={form.last_creative_added_date||''} onChange={set('last_creative_added_date')} />
        </div>
        <Textarea label="Comments" value={form.comments||''} onChange={set('comments')} />
      </Modal>}
    </div>
  )
}

// ── USER MANAGEMENT ──────────────────────────────────────────
export function UserManagement() {
  const { user: currentUser } = useAuth()
  const canApprove = currentUser?.role === 'owner' || (typeof currentUser?.permissions === 'object' && currentUser?.permissions?.can_approve === true)
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)
  const [viewRow, setViewRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [inviteLink, setInviteLink] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try { setUsers(await api.getUsers()) }
    catch (err) { toast.error(err.message) }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  function openAdd() { setForm({ role: 'user', permissions: {} }); setModal('add') }
  function openEdit(u) { setForm({ ...u, password: '', permissions: (typeof u.permissions === 'object' ? u.permissions : {}) }); setModal(u) }

  async function save() {
    setSaving(true)
    try {
      if (modal === 'add') {
        if (!form.email) { toast.error('Email required'); setSaving(false); return }
        const created = await api.createUser(form)
        const link = `${window.location.origin}/invite/${created.invite_token}`
        setInviteLink({ email: created.email, link })
        toast.success('Invite created!')
        load()
      } else {
        await api.updateUser(modal.id, form)
        toast.success('User updated!')
        setModal(null); load()
      }
    } catch (err) { toast.error(err.message) }
    setSaving(false)
  }

  async function reinvite(u) {
    try {
      const r = await api.reinvite(u.id)
      const link = `${window.location.origin}/invite/${r.invite_token}`
      setInviteLink({ email: r.email, link })
      load()
    } catch (err) { toast.error(err.message) }
  }

  function copyLink(link) {
    navigator.clipboard.writeText(link).then(() => toast.success('Link copied!')).catch(() => toast.error('Could not copy'))
  }

  async function del(id) {
    if (!window.confirm('Delete this user?')) return
    try { await api.deleteUser(id); toast.success('Deleted'); load() }
    catch (err) { toast.error(err.message) }
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))
  const roleOptions = Object.entries(ROLES).map(([k,v]) => ({ value:k, label:`${v.icon} ${v.label}` }))
  const needsMarket = ['funnel_builder','creative_editor','ad_copy_writer','ad_scheduler']

  function normalizePerm(p) {
    // Convert legacy boolean to object form
    if (p === true) return { view:true, edit:true, create:true, delete:true, generate:true, audit:true, wetracked:true, checklist:true, adapt:true }
    if (typeof p === 'object' && p) return p
    return {}
  }

  function toggleSection(section) {
    setForm(f => {
      const perms = { ...(f.permissions || {}) }
      const cur = normalizePerm(perms[section.id])
      const isOn = !!cur.view || Object.values(cur).some(Boolean)
      if (isOn) {
        delete perms[section.id]  // turn whole section off
      } else {
        perms[section.id] = { view: true }  // turn on with view by default
      }
      return { ...f, permissions: perms }
    })
  }

  function toggleAction(sectionId, action) {
    setForm(f => {
      const perms = { ...(f.permissions || {}) }
      const cur = { ...normalizePerm(perms[sectionId]) }
      cur[action] = !cur[action]
      // view is required - if turning off view, keep section but warn via UI; if no actions left, remove
      if (action !== 'view' && cur[action] && !cur.view) cur.view = true  // any action implies view
      perms[sectionId] = cur
      return { ...f, permissions: perms }
    })
  }

  function setFullAccess(section) {
    setForm(f => {
      const perms = { ...(f.permissions || {}) }
      const all = {}
      section.actions.forEach(a => { all[a] = true })
      perms[section.id] = all
      return { ...f, permissions: perms }
    })
  }

  return (
    <div style={{padding:20}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
        <span className="toolbar-title">👥 User Management</span>
        <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
      </div>
      {loading ? <Empty icon="⏳" title="Loading..." /> : (
        <div className="users-grid">
          {users.map(u => {
            const r = ROLES[u.role] || ROLES.pending
            return (
              <div key={u.id} className="user-card">
                <div className="user-avatar">{r.icon}</div>
                <div className="user-info">
                  <div className="user-name">{u.full_name||'(no name)'}</div>
                  <div className="user-email">{u.email}</div>
                </div>
                <div className="user-actions">
                  {u.status === 'invited'
                    ? <span className="badge badge-warning">⏳ Invited</span>
                    : <span className="badge badge-success">✓ Active</span>}
                  {u.assigned_market && <MarketBadge market={u.assigned_market} />}
                  {u.status === 'invited' && (
                    <button className="btn btn-secondary btn-sm" onClick={()=>reinvite(u)}>🔗 Invite Link</button>
                  )}
                  <button className="btn btn-secondary btn-sm" onClick={()=>openEdit(u)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={()=>del(u.id)}>Del</button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {inviteLink && <Modal title="🔗 Invite Link Created" onClose={()=>{setInviteLink(null);setModal(null)}}
        footer={<button className="btn btn-primary" onClick={()=>{setInviteLink(null);setModal(null)}}>Done</button>}>
        <div style={{fontSize:13,color:'var(--text2)',marginBottom:14,lineHeight:1.6}}>
          Send this link to <strong style={{color:'var(--text)'}}>{inviteLink.email}</strong>. They can set their own password. The link expires in <strong style={{color:'var(--gold)'}}>24 hours</strong>.
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <input readOnly value={inviteLink.link} style={{flex:1,background:'var(--bg3)',border:'1px solid var(--border2)',borderRadius:8,color:'var(--accent)',padding:'10px 12px',fontSize:12,fontFamily:'DM Mono,monospace',outline:'none'}} onClick={e=>e.target.select()} />
          <button className="btn btn-primary" onClick={()=>copyLink(inviteLink.link)} style={{whiteSpace:'nowrap'}}>📋 Copy</button>
        </div>
        <div style={{fontSize:11,color:'var(--text3)',marginTop:10}}>Tip: click the link field to select all, or hit Copy.</div>
      </Modal>}

      {modal && !inviteLink && <Modal title={modal==='add'?'Add User':'Edit User'} onClose={()=>setModal(null)}
        footer={<><button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button></>}>
        <Input label="Full Name" value={form.full_name||''} onChange={set('full_name')} />
        {modal === 'add' && <Input label="Email" type="email" value={form.email||''} onChange={set('email')} />}
        {modal === 'add' && <div style={{fontSize:11,color:'var(--text3)',marginTop:-8,marginBottom:12,lineHeight:1.5}}>An invite link will be generated. Send it to the user so they can set their own password. The link expires in 24 hours.</div>}
        {modal !== 'add' && <Input label="New Password (leave blank to keep current)" type="password" value={form.password||''} onChange={set('password')} />}
        <Select label="Assigned Market (optional)" options={['', ...MARKETS]} value={form.assigned_market||''} onChange={set('assigned_market')} />
        {canApprove && <div style={{marginBottom:12}}>
          <label style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.6px',fontFamily:'DM Mono,monospace',display:'block',marginBottom:6}}>Allowed Markets (new work)</label>
          <div style={{fontSize:11,color:'var(--text3)',marginBottom:8,lineHeight:1.5}}>Not-Started rows only appear in these markets. Work already in progress stays visible regardless. Applies to Funnel, Creatives, Ad Copy.</div>
          <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
            {MARKETS.map(m => {
              const cur = Array.isArray(form.allowed_markets) ? form.allowed_markets : []
              const on = cur.includes(m)
              return (
                <button key={m} type="button" onClick={()=>{
                  const next = on ? cur.filter(x=>x!==m) : [...cur, m]
                  if (modal && modal.id) { api.updateUserMarkets(modal.id, next).then(()=>toast.success('Markets updated')).catch(e=>toast.error(e.message)) }
                  setForm(f=>({...f, allowed_markets: next}))
                }} style={{fontSize:11,fontWeight:700,padding:'4px 10px',borderRadius:6,cursor:'pointer',
                  background: on?'rgba(59,130,246,0.15)':'var(--bg3)',
                  border:'1px solid '+(on?'var(--accent)':'var(--border2)'),
                  color: on?'var(--accent)':'var(--text3)'}}>{m}</button>
              )
            })}
          </div>
        </div>}
        <div style={{display:'flex',gap:12}}>
          <div style={{flex:1}}>
            <Select label="Pay Type" options={['','hourly','weekly','biweekly','monthly']} value={form.pay_type||''} onChange={set('pay_type')} />
          </div>
          <div style={{flex:1}}>
            <Select label="Pay Basis" options={['hourly','fixed']} value={form.pay_basis||'hourly'} onChange={set('pay_basis')} />
          </div>
          <div style={{flex:1}}>
            <Input label={(form.pay_type==='monthly'||form.pay_basis==='fixed')?'Fixed Amount (USD)':'Hourly Rate (USD)'} type="number" step="0.01" value={form.pay_rate||''} onChange={set('pay_rate')} />
          </div>
        </div>
        {form.pay_basis==='fixed' && form.pay_type!=='monthly' && <Input label="Expected Working Days Per Period" type="number" step="0.5" value={form.expected_days||5} onChange={set('expected_days')} />}
        {modal !== null && form.role !== 'owner' && (() => {
          const canApp = (form.permissions||{}).can_approve === true
          return (
          <div onClick={()=>setForm(f=>({...f,permissions:{...(f.permissions||{}),can_approve:!canApp}}))}
            style={{marginTop:4,marginBottom:12,display:'flex',alignItems:'center',gap:10,padding:'11px 12px',borderRadius:10,cursor:'pointer',border:`1px solid ${canApp?'rgba(16,185,129,0.4)':'var(--border)'}`,background:canApp?'rgba(16,185,129,0.08)':'var(--bg3)'}}>
            <div style={{width:32,height:18,borderRadius:9,flexShrink:0,background:canApp?'var(--accent3)':'#CBD5E1',position:'relative',transition:'background 0.2s'}}>
              <div style={{position:'absolute',top:2,left:canApp?16:2,width:14,height:14,borderRadius:'50%',background:'#fff',transition:'left 0.2s'}}/>
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>Operations Manager (Can Approve)</div>
              <div style={{fontSize:11,color:'var(--text3)'}}>Allows this user to approve work in all sections</div>
            </div>
          </div>
          )
        })()}
        {modal !== null && form.role !== 'owner' && (
          <div style={{marginTop:4}}>
            <label style={{display:'block',fontSize:10,fontWeight:600,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'0.7px',fontFamily:'DM Mono,monospace',marginBottom:6}}>Section Permissions</label>
            <div style={{fontSize:11,color:'var(--text3)',marginBottom:12}}>Tick a section to enable it, then choose which actions the user can perform.</div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {ALL_SECTIONS.filter(s=>s.id!=='company').map(s => {
                const cur = normalizePerm((form.permissions || {})[s.id])
                const sectionOn = !!cur.view || Object.values(cur).some(Boolean)
                return (
                  <div key={s.id} style={{borderRadius:10,border:`1px solid ${sectionOn?'rgba(59,130,246,0.3)':'var(--border)'}`,background:sectionOn?'rgba(59,130,246,0.06)':'var(--bg3)',overflow:'hidden'}}>
                    {/* Section header */}
                    <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',cursor:'pointer',borderBottom:sectionOn?'1px solid var(--border)':'none'}} onClick={()=>toggleSection(s)}>
                      <div style={{width:16,height:16,borderRadius:4,border:`2px solid ${sectionOn?'var(--accent)':'var(--border2)'}`,background:sectionOn?'var(--accent)':'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        {sectionOn && <span style={{fontSize:10,color:'#fff',fontWeight:700,lineHeight:1}}>✓</span>}
                      </div>
                      <span style={{fontSize:12,fontFamily:'monospace'}}>{s.icon}</span>
                      <span style={{fontSize:13,fontWeight:600,color:sectionOn?'var(--text)':'var(--text2)',flex:1}}>{s.label}</span>
                      {sectionOn && s.actions.length>1 && (
                        <button onClick={(e)=>{e.stopPropagation();setFullAccess(s)}} style={{fontSize:10,color:'var(--accent)',background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.2)',borderRadius:5,padding:'2px 8px',cursor:'pointer',fontFamily:'DM Mono,monospace'}}>Full Access</button>
                      )}
                    </div>
                    {/* Action checkboxes */}
                    {sectionOn && s.actions.length>1 && (
                      <div style={{display:'flex',flexWrap:'wrap',gap:6,padding:'10px 12px'}}>
                        {s.actions.map(a => {
                          const on = !!cur[a]
                          return (
                            <div key={a} onClick={()=>toggleAction(s.id,a)} style={{display:'flex',alignItems:'center',gap:6,padding:'4px 10px',borderRadius:20,cursor:'pointer',background:on?'rgba(16,185,129,0.15)':'var(--bg2)',border:`1px solid ${on?'rgba(16,185,129,0.3)':'var(--border)'}`,transition:'all 0.15s'}}>
                              <div style={{width:13,height:13,borderRadius:3,border:`2px solid ${on?'var(--accent3)':'var(--border2)'}`,background:on?'var(--accent3)':'transparent',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                {on && <span style={{fontSize:8,color:'#000',fontWeight:700,lineHeight:1}}>✓</span>}
                              </div>
                              <span style={{fontSize:11,color:on?'var(--text)':'var(--text3)',fontWeight:on?600:400}}>{ACTION_LABELS[a]}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            <button onClick={()=>setForm(f=>({...f,permissions:{}}))} style={{marginTop:10,fontSize:11,color:'var(--text3)',background:'none',border:'none',cursor:'pointer',textDecoration:'underline'}}>
              Clear all permissions
            </button>
          </div>
        )}
      </Modal>}
    </div>
  )
}
